import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Disclosure } from "@headlessui/react";
import { ArrowDownIcon, ArrowUpIcon, XIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronUpIcon, Cross2Icon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  ColumnFiltersState,
  Table as ReactTable,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DatePicker } from "@tremor/react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  FieldArrayWithId,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as z from "zod";
import { useAnalytics } from "../../analytics-context";
import { Badge, Spinner, SubmitButton } from "../../components";
import { OvalSpinner } from "../../components/loading";
import { Modal } from "../../components/Modal";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { DataTableFacetedFilter } from "../../components/ui/table-helpers/data-table-faceted-filter";
import { DataTablePagination } from "../../components/ui/table-helpers/data-table-pagination";
import {
  CalculateEstimate,
  CalculateEstimateVariables,
  CalculateEstimate_calculateEstimate as EstimateCalculation,
} from "../../generated/CalculateEstimate";
import {
  CreateDraftEstimateFromForm,
  CreateDraftEstimateFromFormVariables,
} from "../../generated/CreateDraftEstimateFromForm";
import {
  GetEstimate_estimate as Estimate,
  GetEstimate,
  GetEstimateVariables,
} from "../../generated/GetEstimate";
import {
  GetFeeSchedules_feeSchedules as FeeSchedule,
  GetFeeSchedules,
  GetFeeSchedulesVariables,
} from "../../generated/GetFeeSchedules";
import {
  GetLocationChargeTemplates_chargeTemplates as ChargeTemplate,
  GetLocationChargeTemplates,
  GetLocationChargeTemplatesVariables,
  GetLocationChargeTemplates_chargeTemplates,
} from "../../generated/GetLocationChargeTemplates";
import { GetMe_me_organization_providerServiceConfiguration as ProviderServiceConfiguration } from "../../generated/GetMe";
import {
  GetPatient_patient as IPatient,
  GetPatient_patient_insurancePolicies as InsurancePolicy,
} from "../../generated/GetPatient";
import {
  GetProviders,
  GetProvidersVariables,
  GetProviders_providers as Provider,
} from "../../generated/GetProviders";
import { EligibilityRequestStatus } from "../../generated/globalTypes";
import {
  LookupAllowedAmounts,
  LookupAllowedAmountsVariables,
} from "../../generated/LookupAllowedAmounts";
import {
  RecommendedFeeScheduleForDraftEstimate,
  RecommendedFeeScheduleForDraftEstimateVariables,
} from "../../generated/RecommendedFeeScheduleForDraftEstimate";
import {
  RequestEligibility,
  RequestEligibilityVariables,
} from "../../generated/RequestEligibility";
import { ESTIMATE_FIELDS, LOOKUP_ALLOWED_AMOUNTS } from "../../graphql";
import { useIsMounted } from "../../hooks";
import { useUser } from "../../user-context";
import {
  cn,
  decimalToPercentage,
  formatUSD,
  isDefined,
  mapNullable,
  percentageToDecimal,
  toCents,
  toDollarStr,
} from "../../utils";
import { EligibilityStatusBadge } from "../appointments";
import {
  CALCULATE_ESTIMATE,
  Chargemaster,
  CodeSearchCommand,
  GET_LOCATION_CHARGE_TEMPLATES,
  buildInsuranceServiceLines,
} from "../appointments/table/estimate-dialog";
import {
  BenefitsDifferAlert,
  ChargesDifferAlert,
  FormSchema,
  InsuranceServiceLine,
  LineItemFormRow,
  PoliciesChangedAlert,
  ResetActions,
  isDefinedInput,
} from "../appointments/table/estimate-form";
import { chargemasterDisplay } from "../chargemaster";
import { GET_FEESCHEDULES } from "../fee-schedules/index";
import { ServiceBenefitHoverCard } from "../patients/insurances/show";
import { NetworkBadge } from "../patients/networkBadge";
import { PlanTypeSelect } from "../patients/plan-type-select";
import { PatientSummary, RETRIEVE_DRAFT_ESTIMATES } from "../patients/profile";
import { GET_PATIENT } from "../patients/show";
import { GET_PROVIDERS } from "../providers/index";
import {
  ChargeTemplateCombobox,
  FeeScheduleCombobox,
} from "../rules/rule-inputs";
import { ChargemasterBadge } from "../shared/visit-bill-display-card";
import {
  InsurancePolicyRow,
  REQUEST_ELIGIBILITY,
} from "../worklists/policies/split-pane";

const GET_ESTIMATE = gql`
  query GetEstimate($id: String!) {
    estimate(where: { id: $id }) {
      id
      chargeTemplate {
        id
        name
      }
      feeSchedule {
        id
        name
      }
      draftEstimateProvider {
        id
        displayName
      }
      draftEstimateDateOfService
      draftEstimateName
      chargesDifferFromBill
      policiesChanged
      estimatedCharges(orderBy: { priority: asc }) {
        id
        chargemaster {
          id
          code
          modifier1
          modifier2
          modifier3
          modifier4
          description
          chargemasterGroupId
        }
        units
        allowedAmount
        coinsurance
        copay
        remainingDeductible
        deductibleApplies
        remainingOutOfPocket
        coverageBenefitId
        estimatedInsurancePolicy {
          id
          active
          insurancePolicyId
        }
        scheduledServiceFee {
          id
          name
          feeSchedule {
            id
            name
          }
        }
      }
      bill {
        id
        charges {
          id
          customCode
          chargemaster {
            id
            code
            modifier1
            modifier2
            modifier3
            modifier4
            description
            chargemasterGroupId
          }
          units
          allowedAmount
        }
      }
      recommendedBenefitsDiffer
    }
  }
`;

const CREATE_DRAFT_ESTIMATE = gql`
  ${ESTIMATE_FIELDS}
  mutation CreateDraftEstimateFromForm(
    $inputs: [EstimateCalculationInput!]!
    $policyIds: [EstimatePolicyInput!]!
    $chargeTemplateId: String
    $feeScheduleId: String
    $patientId: String!
    $existingEstimateId: String
    $draftEstimateProviderId: String
    $draftEstimateDateOfService: DateTime
    $draftEstimateName: String
  ) {
    createDraftEstimate(
      inputs: $inputs
      policyIds: $policyIds
      chargeTemplateId: $chargeTemplateId
      feeScheduleId: $feeScheduleId
      patientId: $patientId
      existingEstimateId: $existingEstimateId
      draftEstimateProviderId: $draftEstimateProviderId
      draftEstimateDateOfService: $draftEstimateDateOfService
      draftEstimateName: $draftEstimateName
    ) {
      estimate {
        id
        status
        type
        ...EstimateFields
        estimatedCharges(orderBy: { priority: asc }) {
          id
          chargemaster {
            id
            code
            modifier1
            modifier2
            modifier3
            modifier4
            chargemasterGroupId
          }
          units
          allowedAmount
          copayAmount
          coinsuranceAmount
          deductibleAmount
          outOfPocketAmount
          insuranceAmount
        }
      }
    }
  }
`;

export const ChargeTemplateSelector: React.FC<{
  onSelect: (templateId: string) => void;
  selectedTemplate: string | null;
  chargeTemplates: GetLocationChargeTemplates_chargeTemplates[];
}> = ({ onSelect, selectedTemplate, chargeTemplates }) => {
  const [searchQuery, setSearchQuery] = useState("");

  const allTemplates = [
    {
      id: "none",
      name: "Blank Estimate",
      chargeTemplateCharges: [],
    },
    ...chargeTemplates,
  ];

  const filteredTemplates = allTemplates.filter((template) => {
    if (template.id === "none") return true;
    if (searchQuery === "") return true;

    // Search in template name and CPT codes
    const cptCodes = template.chargeTemplateCharges.map(
      (charge) => charge.chargemasterGroup.primaryChargemaster.code
    );

    return (
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      cptCodes.some((code) =>
        code.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  });

  return (
    <div className="space-y-4">
      <Input
        type="search"
        placeholder="Search by name or CPT code..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full rounded-md"
      />
      <ScrollArea className="h-[300px] w-full rounded-md border bg-gray-50">
        <div className="grid grid-cols-2 gap-4 p-4">
          {filteredTemplates.map((template) => (
            <Card
              key={template.id}
              className={`cursor-pointer transition-all hover:bg-accent ${
                selectedTemplate === template.id
                  ? "border-primary border-2"
                  : ""
              }`}
              onClick={() => onSelect(template.id)}
            >
              <CardHeader className="p-4">
                <CardTitle className="text-sm">{template.name}</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                {template.chargeTemplateCharges.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Create estimate without a charge template
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    CPT Codes:{" "}
                    {template.chargeTemplateCharges
                      .map(
                        (charge) =>
                          `${chargemasterDisplay(
                            charge.chargemasterGroup.primaryChargemaster
                          )} x ${charge.units}`
                      )
                      .join(", ")}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

const RECOMMENDED_FEE_SCHEDULE_FOR_DRAFT_ESTIMATE = gql`
  query RecommendedFeeScheduleForDraftEstimate(
    $patientId: String!
    $providerId: String!
  ) {
    recommendedFeeScheduleForDraftEstimate(
      patientId: $patientId
      providerId: $providerId
    ) {
      id
      name
    }
  }
`;

export const CreateEstimateDialog: React.FC<{
  patient: IPatient;
  onComplete: (formData: {
    estimateName: string;
    provider: string;
    date: Date | undefined;
    templateId: string | null;
    recommendedFeeScheduleId: string | null;
  }) => void;
  open?: boolean;
  setOpen?: (open: boolean) => void;
}> = ({
  patient,
  onComplete,
  open: controlledOpen,
  setOpen: controlledSetOpen,
}) => {
  const analytics = useAnalytics();
  const client = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [provider, setProvider] = useState("");
  const [date, setDate] = useState<Date>();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(
    "none"
  );
  const [estimateName, setEstimateName] = useState("");
  const [hasEditedName, setHasEditedName] = useState(false);
  const [internalOpen, setInternalOpen] = useState(false);

  // Determine if we're using controlled or uncontrolled state
  const isControlled =
    controlledOpen !== undefined && controlledSetOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? controlledSetOpen : setInternalOpen;

  // Track when dialog is opened
  useEffect(() => {
    if (open) {
      analytics?.track("Draft Estimate Form Opened", {
        patientId: patient.id,
      });
    }
  }, [open, patient.id]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const recommendedFeeScheduleResult = await client.query<
        RecommendedFeeScheduleForDraftEstimate,
        RecommendedFeeScheduleForDraftEstimateVariables
      >({
        query: RECOMMENDED_FEE_SCHEDULE_FOR_DRAFT_ESTIMATE,
        variables: {
          patientId: patient.id,
          providerId: provider,
        },
      });
      onComplete({
        estimateName,
        provider,
        date,
        templateId: selectedTemplate,
        recommendedFeeScheduleId:
          recommendedFeeScheduleResult.data
            ?.recommendedFeeScheduleForDraftEstimate?.id ?? null,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const user = useUser()!;
  const chargeTemplatesResult = useQuery<
    GetLocationChargeTemplates,
    GetLocationChargeTemplatesVariables
  >(GET_LOCATION_CHARGE_TEMPLATES, {
    variables: {
      locationId: user.activeLocation.id,
    },
  });

  const providersResult = useQuery<GetProviders, GetProvidersVariables>(
    GET_PROVIDERS,
    {
      variables: {
        organizationId: user.organization.id,
        locationId: user.activeLocation.id,
      },
    }
  );

  // Handler for template selection
  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);

    // Only update the name if the user hasn't manually edited it or if the name is empty
    if ((!hasEditedName && templateId !== "none") || estimateName === "") {
      const selectedChargeTemplate =
        chargeTemplatesResult.data?.chargeTemplates.find(
          (template) => template.id === templateId
        );
      if (selectedChargeTemplate) {
        setEstimateName(selectedChargeTemplate.name);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {!isControlled && (
          <DialogTrigger asChild>
            <Button size="sm">Create Estimate</Button>
          </DialogTrigger>
        )}
        <DialogContent className="sm:max-w-[625px] max-h-[90vh] overflow-y-auto">
          <div className="grid gap-6 py-4">
            <div className="space-y-2">
              <Label className="text-base font-semibold">Charge Template</Label>
              <ChargeTemplateSelector
                onSelect={handleTemplateSelect}
                selectedTemplate={selectedTemplate}
                chargeTemplates={
                  chargeTemplatesResult.data?.chargeTemplates ?? []
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="estimateName" className="text-base">
                Name{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  (optional)
                </span>
              </Label>
              <Input
                id="estimateName"
                value={estimateName}
                onChange={(e) => {
                  // Limit to 25 characters
                  if (e.target.value.length <= 25) {
                    setEstimateName(e.target.value);
                    setHasEditedName(true); // Mark that user has edited the name
                  }
                }}
                placeholder="Enter estimate name"
              />
              <p className="text-xs text-muted-foreground">
                {estimateName.length}/25 characters
              </p>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="provider" className="text-base">
                  Provider{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    (optional)
                  </span>
                </Label>
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {providersResult.data?.providers.map((provider) => (
                      <SelectItem
                        key={provider.id}
                        value={provider.id}
                        className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                      >
                        {provider.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-base">Insurance Policies</Label>
                {patient.insurancePolicies.map((insurancePolicy) => (
                  <DraftEstimateInsurancePolicyDisclosureRow
                    key={insurancePolicy.id}
                    insurancePolicy={insurancePolicy}
                    patientId={patient.id}
                    providerServiceConfigurations={
                      user.organization.providerServiceConfiguration
                    }
                    selectedProviderId={provider}
                  />
                ))}
              </div>
              <div className="space-y-2">
                <Label htmlFor="date" className="text-base">
                  Date of Service{" "}
                  <span className="text-sm font-normal text-muted-foreground">
                    (optional)
                  </span>
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              "Generate Estimate"
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DraftEstimateWizardDialog: React.FC<
  React.PropsWithChildren<{
    patient: IPatient;
    existingEstimateId?: string;
    selectedProvider?: string | null;
    selectedDate?: Date | null;
    selectedTemplate?: string | null;
    selectedEstimateName?: string | null;
    selectedRecommendedFeeScheduleId?: string | null;
    setOpen: (open: boolean) => void;
    onComplete?: () => void;
  }>
> = ({
  patient,
  existingEstimateId,
  selectedProvider = null,
  selectedDate = null,
  selectedTemplate = null,
  selectedEstimateName = null,
  selectedRecommendedFeeScheduleId = null,
  setOpen,
  onComplete,
}) => {
  const user = useUser()!;
  const chargeTemplatesResult = useQuery<
    GetLocationChargeTemplates,
    GetLocationChargeTemplatesVariables
  >(GET_LOCATION_CHARGE_TEMPLATES, {
    variables: {
      locationId: user.activeLocation.id,
    },
  });

  const feeSchedulesResult = useQuery<
    GetFeeSchedules,
    GetFeeSchedulesVariables
  >(GET_FEESCHEDULES, {
    variables: {
      locationId: user.activeLocation.id,
      pending: false,
    },
  });

  const providersResult = useQuery<GetProviders, GetProvidersVariables>(
    GET_PROVIDERS,
    {
      variables: {
        organizationId: user.organization.id,
        locationId: user.activeLocation.id,
      },
    }
  );

  const estimate = useQuery<GetEstimate, GetEstimateVariables>(GET_ESTIMATE, {
    variables: {
      id: existingEstimateId!,
    },
    skip: !existingEstimateId,
  });

  const loading =
    chargeTemplatesResult.loading ||
    feeSchedulesResult.loading ||
    providersResult.loading ||
    estimate.loading;

  const chargeTemplates = chargeTemplatesResult.data?.chargeTemplates ?? [];
  const feeSchedules = feeSchedulesResult.data?.feeSchedules ?? [];

  return (
    <Modal open={true} setOpen={setOpen} size="xl" className="!px-0 !pb-0">
      <div className="min-w-1/2">
        <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={() => {
              setOpen(false);
              onComplete?.();
            }}
          >
            <span className="sr-only">Close</span>
            <XIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        {loading ? (
          <div className="flex w-full h-[40vh]">
            <div className="m-auto flex items-center gap-2">
              Loading Estimate <OvalSpinner className="w-8" />
            </div>
          </div>
        ) : (
          <CreateDraftEstimateWizard
            patient={patient}
            existingEstimate={estimate.data?.estimate ?? null}
            chargeTemplates={chargeTemplates}
            feeSchedules={feeSchedules}
            onComplete={onComplete}
            providers={providersResult.data?.providers ?? []}
            selectedProvider={selectedProvider}
            selectedDate={selectedDate}
            selectedTemplate={selectedTemplate}
            selectedEstimateName={selectedEstimateName}
            selectedRecommendedFeeScheduleId={selectedRecommendedFeeScheduleId}
          />
        )}
      </div>
    </Modal>
  );
};

const CreateDraftEstimateWizard: React.FC<{
  patient: IPatient;
  chargeTemplates: ChargeTemplate[];
  existingEstimate: Estimate | null;
  selectedProvider: string | null;
  selectedDate: Date | null;
  selectedTemplate: string | null;
  selectedEstimateName: string | null;
  selectedRecommendedFeeScheduleId: string | null;
  feeSchedules: FeeSchedule[];
  providers: Provider[];
  onComplete?: () => void;
}> = ({
  patient,
  chargeTemplates,
  existingEstimate,
  selectedProvider,
  selectedDate,
  selectedTemplate,
  selectedEstimateName,
  selectedRecommendedFeeScheduleId,
  feeSchedules,
  providers,
  onComplete,
}) => {
  const [provider, setProvider] = useState<string | null>(
    existingEstimate?.draftEstimateProvider?.id ?? selectedProvider ?? null
  );
  const [date, setDate] = useState<Date | null>(
    existingEstimate?.draftEstimateDateOfService
      ? new Date(existingEstimate.draftEstimateDateOfService)
      : selectedDate ?? null
  );
  const [templateId, setTemplateId] = useState<string | null>(
    existingEstimate?.chargeTemplate?.id ?? selectedTemplate ?? null
  );
  const [estimateName, setEstimateName] = useState<string | null>(
    existingEstimate?.draftEstimateName ?? selectedEstimateName ?? null
  );

  return (
    <div className="min-h-[30vh] flex flex-col">
      <div className="px-8 border-b-2 pb-2">
        <DraftEstimateAppointmentDisplay
          key={patient.id}
          patient={patient}
          providers={providers}
          setProvider={setProvider}
          provider={provider}
          date={date}
          setDate={setDate}
          setName={setEstimateName}
          name={estimateName}
        />
      </div>
      <div className="py-4">
        <DraftEstimateDialogInner
          templateId={templateId}
          provider={provider}
          date={date}
          selectedRecommendedFeeScheduleId={selectedRecommendedFeeScheduleId}
          estimateName={estimateName}
          patient={patient}
          chargeTemplates={chargeTemplates}
          initialExistingEstimate={existingEstimate}
          feeSchedules={feeSchedules}
          onComplete={onComplete}
        />
      </div>
    </div>
  );
};

export const DraftEstimateAppointmentDisplay: React.FC<{
  patient: IPatient;
  setProvider: (providerId: string) => void;
  setDate: (dateOfService: Date) => void;
  setName: (name: string) => void;
  provider: string | null;
  date: Date | null;
  name: string | null;
  providers: Provider[];
}> = ({
  patient,
  providers,
  setProvider,
  setDate,
  setName,
  provider,
  date,
  name,
}) => {
  return (
    <div className="w-full grid gap-5">
      <div className="col-span-full">
        <PatientSummary patient={{ ...patient, accounts: [] }} />
      </div>

      <div className="grid grid-cols-3 gap-5">
        <div>
          <div className="text-sm text-gray-500 mb-1">Date of Service</div>
          <div>
            <DatePicker
              value={date ?? undefined}
              onValueChange={(date) => {
                if (date) {
                  const newDate = new Date(date);
                  setDate(newDate);
                }
              }}
              className="w-full"
              placeholder="Select a date"
            />
          </div>
        </div>

        <div>
          <div className="text-sm text-gray-500 mb-1">Provider</div>
          <Select
            value={provider ?? undefined}
            onValueChange={(value: string) => {
              setProvider(value);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a provider" />
            </SelectTrigger>
            <SelectContent>
              {providers.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  {provider.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="text-sm text-gray-500 mb-1 flex justify-between items-center">
            <span>Name</span>
            <span className="text-xs text-muted-foreground">
              {name?.length ?? 0}/25
            </span>
          </div>
          <Input
            value={name ?? ""}
            onChange={(e) => {
              if (e.target.value.length <= 25) {
                setName(e.target.value);
              }
            }}
            placeholder="Add a name for this estimate"
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export const DraftEstimateDialogInner: React.FC<{
  patient: IPatient;
  initialExistingEstimate: Estimate | null;
  chargeTemplates: ChargeTemplate[];
  feeSchedules: FeeSchedule[];
  templateId: string | null;
  provider: string | null;
  estimateName: string | null;
  date: Date | null;
  selectedRecommendedFeeScheduleId: string | null;
  onComplete?: () => void;
}> = ({
  patient,
  initialExistingEstimate,
  chargeTemplates,
  feeSchedules,
  templateId,
  provider,
  date,
  estimateName,
  selectedRecommendedFeeScheduleId,
  onComplete,
}) => {
  const [existingEstimate, setExistingEstimate] = useState(
    initialExistingEstimate ?? null
  );

  const existingEstimatePolicies = existingEstimate
    ? Array.from(
        existingEstimate?.estimatedCharges.reduce(
          (
            acc: Map<
              string | null,
              {
                id: string | null;
                active: boolean;
              }
            >,
            ec
          ) => {
            if (
              acc.has(ec.estimatedInsurancePolicy?.insurancePolicyId ?? null)
            ) {
              return acc;
            }
            acc.set(ec.estimatedInsurancePolicy?.insurancePolicyId ?? null, {
              id: ec.estimatedInsurancePolicy?.insurancePolicyId ?? null,
              active: ec.estimatedInsurancePolicy?.active ?? true,
            });
            return acc;
          },
          new Map()
        )
      ).map(([_, { id, active }]) => ({ id, active }))
    : null;

  // If there's an existing estimate, default the policies

  let policies: { id: string | null; active: boolean }[] =
    existingEstimatePolicies ?? patient.insurancePolicies;
  // If there are no policies, default to cash
  if (policies.length === 0) {
    policies = [
      {
        id: null,
        active: true,
      },
    ];
  }

  let defaultInsurancePolicyIds = policies
    .filter((p) => p.active && p.id !== null)
    .map((p) => p.id!) as string[];

  if (existingEstimate) {
    defaultInsurancePolicyIds = Array.from(
      new Set(
        existingEstimate.estimatedCharges
          .filter(
            (charge) => charge.estimatedInsurancePolicy?.insurancePolicyId
          )
          .map((charge) => charge.estimatedInsurancePolicy!.insurancePolicyId!)
      )
    );
  }

  const billedCharges = existingEstimate?.bill?.charges ?? [];

  // First look for the template passed as templateId
  // Then look for the template used for any existing estimates
  const defaultChargeTemplate = existingEstimate?.chargeTemplate
    ? chargeTemplates.find(
        (ct) => ct.id === existingEstimate.chargeTemplate?.id
      ) ?? null
    : templateId
    ? chargeTemplates.find((ct) => ct.id === templateId) ?? null
    : null;
  const defaultFeeSchedule = selectedRecommendedFeeScheduleId
    ? feeSchedules.find((fs) => fs.id === selectedRecommendedFeeScheduleId) ??
      null
    : existingEstimate?.feeSchedule
    ? feeSchedules.find((fs) => fs.id === existingEstimate?.feeSchedule?.id) ??
      null
    : null;

  // If there's an existing estimate with charges, find the first policy id
  let primaryEstimatePolicyId =
    existingEstimate?.estimatedCharges?.at(0)?.estimatedInsurancePolicy
      ?.insurancePolicyId ?? null;

  let defaultUniqueCharges: {
    chargemaster: Chargemaster | null;
    units: number;
  }[] = [];
  if (existingEstimate?.estimatedCharges) {
    // Else use the estimate charges
    defaultUniqueCharges = existingEstimate.estimatedCharges.filter(
      (charge) =>
        (charge.estimatedInsurancePolicy?.insurancePolicyId ?? null) ===
        primaryEstimatePolicyId
    );
  } else if (billedCharges.length > 0) {
    defaultUniqueCharges = billedCharges.map((charge) => ({
      chargemaster: charge.chargemaster!,
      units: charge.units!,
      allowedAmount: charge.allowedAmount,
    }));
  } else if (defaultChargeTemplate) {
    // Else use the charge tempalte charges
    defaultUniqueCharges = defaultChargeTemplate.chargeTemplateCharges.map(
      (c) => ({
        ...c,
        description: c.chargemasterGroup?.description ?? null,
        chargemaster: c.chargemasterGroup.primaryChargemaster,
      })
    );
  }
  const defaultChargesWithChargemaster = defaultUniqueCharges.filter((c) =>
    isDefined(c.chargemaster)
  );

  const defaultInsuranceServiceLines = buildInsuranceServiceLines({
    policies,
    charges: defaultChargesWithChargemaster.map((charge) => ({
      chargemaster: charge.chargemaster!,
      units: charge.units.toString(),
    })),
    estimatedCharges: existingEstimate?.estimatedCharges ?? [],
  });

  return (
    <DraftEstimateForm
      patient={patient}
      key={existingEstimate?.id || patient.id}
      existingEstimate={existingEstimate}
      defaultChargeTemplate={defaultChargeTemplate}
      chargeTemplates={chargeTemplates}
      feeSchedules={feeSchedules}
      defaultInsuranceServiceLines={defaultInsuranceServiceLines}
      defaultInsurancePolicyIds={defaultInsurancePolicyIds}
      defaultFeeSchedule={defaultFeeSchedule}
      providerId={provider}
      date={date}
      estimateName={estimateName}
      onComplete={onComplete}
      resetForm={() => {
        setExistingEstimate(null);
      }}
    />
  );
};

export const DraftEstimateForm: React.FC<{
  patient: IPatient;
  existingEstimate: Estimate | null;
  defaultChargeTemplate: ChargeTemplate | null;
  chargeTemplates: ChargeTemplate[];
  defaultFeeSchedule: FeeSchedule | null;
  feeSchedules: FeeSchedule[];
  defaultInsuranceServiceLines: InsuranceServiceLine[];
  defaultInsurancePolicyIds: string[];
  providerId: string | null;
  date: Date | null;
  estimateName: string | null;
  resetForm: () => void;
  onComplete?: () => void;
}> = ({
  patient,
  existingEstimate,
  defaultChargeTemplate,
  chargeTemplates,
  defaultFeeSchedule,
  feeSchedules,
  defaultInsuranceServiceLines,
  defaultInsurancePolicyIds,
  providerId,
  date,
  estimateName,
  resetForm,
  onComplete,
}) => {
  const analytics = useAnalytics();
  const apollo = useApolloClient();
  const [codeSearchOpen, setCodeSearchOpen] = useState(false);

  const chargesDifferFromBill = !!existingEstimate?.chargesDifferFromBill;
  const policiesChanged = !!existingEstimate?.policiesChanged;
  const [chargesDifferAlertOpen, setChargesDifferAlertOpen] = useState(
    chargesDifferFromBill
  );
  const [benefitsDifferAlertOpen, setBenefitsDifferAlertOpen] = useState(
    !!existingEstimate?.recommendedBenefitsDiffer
  );

  const [policiesChangedAlertOpen, setPoliciesChangedAlertOpen] =
    useState(policiesChanged);

  const [estimate, setEstimate] = useState<
    Omit<EstimateCalculation, "__typename">
  >({
    insuranceResponsibilities: [],
    totalPatientResponsibility: 0,
    copayAmount: 0,
    coinsuranceAmount: 0,
    deductibleAmount: 0,
    outOfPocketAmount: 0,
    otherAmount: 0,
  });

  const [createDraftEstimate, createDraftEstimateResult] = useMutation<
    CreateDraftEstimateFromForm,
    CreateDraftEstimateFromFormVariables
  >(CREATE_DRAFT_ESTIMATE);

  const defaultCharges = defaultInsuranceServiceLines
    .at(0)
    ?.serviceLines?.map((line) => ({
      chargemaster: {
        id: line.chargemaster.id,
        code: line.chargemaster.code,
        description: line.chargemaster.description,
        modifier1: line.chargemaster.modifier1,
        modifier2: line.chargemaster.modifier2,
        modifier3: line.chargemaster.modifier3,
        modifier4: line.chargemaster.modifier4,
        chargemasterGroupId: line.chargemaster.chargemasterGroupId,
      },
      units: line.units,
    }));

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    reValidateMode: "onChange",
    defaultValues: {
      charges: defaultCharges,
      chargeTemplateId: defaultChargeTemplate?.id,
      feeScheduleId: defaultFeeSchedule?.id,
      //   insurancePolicyIds: defaultInsurancePolicyIds,
      insuranceServiceLines: defaultInsuranceServiceLines.map((policy) => ({
        policyId: policy.insurancePolicyId,
        enabled: defaultInsurancePolicyIds.includes(policy.insurancePolicyId)
          ? "true"
          : "false",
        serviceLines: policy.serviceLines.map((line) => ({
          existing: !!existingEstimate,
          chargemaster: {
            ...line.chargemaster,
            description: line.chargemaster.description ?? undefined,
            chargemasterGroupId:
              line.chargemaster.chargemasterGroupId ?? undefined,
          },
          units: line.units?.toString(),
          coverageBenefitId: line.coverageBenefitId ?? undefined,
          copay: mapNullable(toDollarStr)(line.copay) ?? undefined,
          coinsurance:
            mapNullable(decimalToPercentage)(line.coinsurance?.toString()) ??
            undefined,
          remainingDeductible:
            mapNullable(toDollarStr)(line.remainingDeductible) ?? undefined,
          deductibleApplies: line.deductibleApplies?.toString(),
          remainingOutOfPocket:
            mapNullable(toDollarStr)(line.remainingOutOfPocket) ?? undefined,
          allowedAmount:
            mapNullable((allowedAmount: number) =>
              toDollarStr(allowedAmount / (line.units ?? 1))
            )(line.allowedAmount) ?? undefined,
        })),
      })),
    },
  });

  const data = form.watch();

  const allPolicyIds = data.insuranceServiceLines.map(
    (policy) => policy.policyId
  );
  const selectedPolicyIds = data.insuranceServiceLines
    .filter((policy) => policy.enabled === "true")
    .map((policy) => policy.policyId);
  const isValid =
    selectedPolicyIds.length > 0 &&
    data.charges.length > 0 &&
    data.insuranceServiceLines.every(
      (policy) =>
        policy.enabled === "false" ||
        policy.serviceLines.every(
          (line) =>
            isDefinedInput(line.chargemaster.id) &&
            isDefinedInput(line.units) &&
            isDefinedInput(line.allowedAmount)
        )
    );
  const primaryId = selectedPolicyIds.at(0);
  const feeScheduleId = data.feeScheduleId;
  const insuranceServiceLines = form.watch("insuranceServiceLines");
  const primaryPolicy = insuranceServiceLines.find(
    (policy) => policy.policyId === primaryId
  );

  const [isCalculating, setIsCalculating] = useState(false);
  const latestRequestRef = useRef<number | null>(null);

  useEffect(() => {
    if (isValid) {
      const requestId = Date.now();
      latestRequestRef.current = requestId;
      setIsCalculating(true);

      const primaryServiceLines =
        data.insuranceServiceLines.find(
          (policy) => policy.policyId === primaryId
        )?.serviceLines ?? [];
      apollo
        .query<CalculateEstimate, CalculateEstimateVariables>({
          query: CALCULATE_ESTIMATE,
          fetchPolicy: "network-only",
          variables: {
            policyIds: allPolicyIds.map((id) => {
              const planType = patient.insurancePolicies.find(
                (p) => p.id === id
              )?.planType;
              return {
                insurancePolicyId: id,
                cashPay: !id,
                active: selectedPolicyIds.includes(id),
                planType: planType
                  ? {
                      id: planType.id,
                      insuranceType: planType.insuranceType,
                      medicareType: planType.medicareType,
                      medigapPlanType: planType.medigapPlanType,
                      medicaidType: planType.medicaidType,
                      tricareType: planType.tricareType,
                    }
                  : null,
              };
            }),
            inputs: data.insuranceServiceLines.flatMap((policy) => {
              return policy.serviceLines.map((line) => {
                const primaryLine = primaryServiceLines.find(
                  (psl) => psl.chargemaster.id === line.chargemaster.id
                );
                return {
                  insurancePolicyId: policy.policyId,
                  chargemasterId: line.chargemaster.id,
                  units: parseInt(primaryLine?.units ?? line.units!),
                  allowedAmount: mapNullable((val: string) =>
                    toCents(parseFloat(val))
                  )(primaryLine?.allowedAmount ?? line.allowedAmount)!,
                  copay: mapNullable((val: string) => toCents(parseFloat(val)))(
                    line.copay
                  ),
                  coinsurance: mapNullable((val: string) =>
                    percentageToDecimal(val)
                  )(line.coinsurance),
                  remainingDeductible: mapNullable((val: string) =>
                    toCents(parseFloat(val))
                  )(line.remainingDeductible),
                  deductibleApplies: isDefined(line.deductibleApplies)
                    ? line.deductibleApplies === "true"
                    : null,
                  remainingOutOfPocket: mapNullable((val: string) =>
                    toCents(parseFloat(val))
                  )(line.remainingOutOfPocket),
                  coverageBenefitId: line.coverageBenefitId
                    ? line.coverageBenefitId
                    : null,
                  scheduledServiceFeeId:
                    primaryLine?.scheduledServiceFeeId ??
                    line.scheduledServiceFeeId ??
                    null,
                };
              });
            }),
          },
        })
        .then(({ data }) => {
          if (latestRequestRef.current === requestId) {
            setEstimate(data.calculateEstimate ?? estimate);
            setIsCalculating(false);
          }
        })
        .catch(() => {
          if (latestRequestRef.current === requestId) {
            setIsCalculating(false);
          }
        });
    }
  }, [JSON.stringify(data)]);

  const insurancePolicies = useFieldArray({
    control: form.control,
    name: "insuranceServiceLines",
  });
  const charges = useFieldArray({
    control: form.control,
    name: "charges",
  });

  // Only use the primary's charges
  const estimatedCharges = insurancePolicies.fields.at(0)?.serviceLines ?? [];
  const estimatedChargeChargemasterIds = estimatedCharges
    .map((ec) => ec.chargemaster.chargemasterGroupId)
    .filter(isDefined);

  const removeCharge = (chargemasterId: string) => {
    const chargeValues = form.getValues("charges");
    const insurancePoliciesValues = form.getValues("insuranceServiceLines");
    chargeValues.forEach((charge, idx) => {
      if (charge.chargemaster.id === chargemasterId) {
        charges.remove(idx);
      }
    });

    // Remove the charge from the insurance service lines
    const updatedInsurancePolicies = insurancePoliciesValues.map((policy) => ({
      ...policy,
      serviceLines: policy.serviceLines
        .filter((line) => line.chargemaster.id !== chargemasterId)
        .map((line) => ({
          ...line,
          // If replacing with undefined, doesn't clear out the existing value for the line so use ""
          copay: line.copay ?? "",
          coinsurance: line.coinsurance ?? "",
          remainingDeductible: line.remainingDeductible ?? "",
          deductibleApplies: line.deductibleApplies ?? "",
          remainingOutOfPocket: line.remainingOutOfPocket ?? "",
          allowedAmount: line.allowedAmount ?? "",
        })),
    }));
    insurancePolicies.replace(updatedInsurancePolicies);
  };

  const onFeeScheduleChange = (feeScheduleId: string) => {
    const feeSchedule = feeSchedules.find((ct) => ct.id === feeScheduleId);
    // If a fee schedule is selected, update the allowed amounts
    lookupAllowedAmounts(feeSchedule?.id ?? null);
  };

  const onChargeTemplateChange = (chargeTemplateId: string) => {
    const chargeTemplate = chargeTemplates.find(
      (ct) => ct.id === chargeTemplateId
    );
    // If a charge template is selected, update the charges to use the template
    if (chargeTemplate) {
      const templateCharges = chargeTemplate.chargeTemplateCharges;
      form.setValue(
        "charges",
        templateCharges.map((ctc) => ({
          chargemaster: ctc.chargemasterGroup.primaryChargemaster,
        }))
      );

      const updatedInsurancePolicies = form
        .getValues("insuranceServiceLines")
        .map((policy) => ({
          ...policy,
          // Default line items to empty
          serviceLines: templateCharges.map((ctc) => ({
            existing: false,
            chargemaster: {
              ...ctc.chargemasterGroup.primaryChargemaster,
              description:
                ctc.chargemasterGroup.primaryChargemaster.description ??
                undefined,
              chargemasterGroupId:
                ctc.chargemasterGroup.primaryChargemaster.chargemasterGroupId ??
                undefined,
            },
            units: ctc.units.toString(),
            coverageBenefitId: undefined,
            copay: undefined,
            coinsurance: undefined,
            remainingDeductible: undefined,
            deductibleApplies: undefined,
            remainingOutOfPocket: undefined,
            allowedAmount: undefined,
            scheduledServiceFee: undefined,
          })),
        }));

      form.resetField("insuranceServiceLines", {
        defaultValue: updatedInsurancePolicies,
      });

      lookupAllowedAmounts(
        feeScheduleId ?? null,
        templateCharges.map((c) => c.chargemasterGroup.id)
      );
    }
  };

  const estimatedChargesWithoutAllowedAmount = estimatedCharges.filter(
    (c) => !isDefinedInput(c.allowedAmount)
  );

  const [allowedAmountsLoading, setAllowedAmountsLoading] = useState(false);

  const lookupAllowedAmounts = (
    feeScheduleId: string | null,
    chargemasterGroupIds?: string[]
  ) => {
    setAllowedAmountsLoading(true);
    apollo
      .query<LookupAllowedAmounts, LookupAllowedAmountsVariables>({
        query: LOOKUP_ALLOWED_AMOUNTS,
        variables: {
          feeScheduleId,
          chargemasterGroupIds:
            chargemasterGroupIds ?? estimatedChargeChargemasterIds,
        },
      })
      .then(({ data, loading }) => {
        const fees = data.lookupAllowedAmounts;

        let i = 0;
        for (const policy of form.getValues("insuranceServiceLines")) {
          let j = 0;
          for (const line of policy.serviceLines) {
            const fee = fees.find(
              (fee) =>
                fee.chargemasterGroupId ===
                line.chargemaster.chargemasterGroupId
            );

            // If only specific fee was looked up then don't clear other fees
            if (
              !fee &&
              line.chargemaster.chargemasterGroupId &&
              chargemasterGroupIds?.includes(
                line.chargemaster.chargemasterGroupId
              )
            ) {
              // Do nothing
            } else {
              const includes =
                !isDefined(chargemasterGroupIds) ||
                (line.chargemaster.chargemasterGroupId &&
                  chargemasterGroupIds?.includes(
                    line.chargemaster.chargemasterGroupId
                  ));
              if (includes) {
                form.setValue(
                  `insuranceServiceLines.${i}.serviceLines.${j}.allowedAmount`,
                  mapNullable(toDollarStr)(fee?.allowedAmount)!
                );
                form.setValue(
                  `insuranceServiceLines.${i}.serviceLines.${j}.scheduledServiceFeeId`,
                  fee?.scheduledServiceFee?.id
                );
                form.setValue(
                  `insuranceServiceLines.${i}.serviceLines.${j}.scheduledServiceFee`,
                  fee?.scheduledServiceFee ?? undefined
                );
              }
            }
            j++;
          }
          i++;
        }
        setAllowedAmountsLoading(false);
      })
      .catch((e) => {
        console.error(e);
        setAllowedAmountsLoading(false);
      });
  };

  const isMounted = useIsMounted();
  //   When the primary policy changes, lookup the allowed amount and set it for all policies
  useEffect(() => {
    if (isMounted() && estimatedChargesWithoutAllowedAmount.length > 0) {
      lookupAllowedAmounts(feeScheduleId ?? null);
    }
  }, [
    feeScheduleId,
    isMounted(),
    // JSON.stringify(estimatedChargesWithoutAllowedAmount.map((c) => c.code)),
  ]);

  // On initial render, lookup allowed amounts if primary policy exists and there are missing allowed amounts
  useEffect(() => {
    if (!isMounted() && estimatedChargesWithoutAllowedAmount.length > 0) {
      lookupAllowedAmounts(feeScheduleId ?? null);
    }
  }, [
    isMounted(),
    feeScheduleId,
    JSON.stringify(
      estimatedChargesWithoutAllowedAmount.map((c) => c.chargemaster.id)
    ),
  ]);

  const totalAllowedAmount = (primaryPolicy?.serviceLines ?? [])
    .filter(
      (line) => isDefined(line.allowedAmount) && line.allowedAmount !== ""
    )
    .reduce((sum, line) => {
      const allowed = 100 * parseFloat(line?.allowedAmount ?? "0");
      const units = parseInt(line?.units ?? "0");
      return sum + units * allowed;
    }, 0);
  const totalInsuranceResponsibility =
    estimate.insuranceResponsibilities.reduce(
      (sum, resp) => sum + resp.insurance,
      0
    );

  const handleConfirm = () => {
    const primaryServiceLines =
      data.insuranceServiceLines.find((policy) => policy.policyId === primaryId)
        ?.serviceLines ?? [];
    const chargeTemplateId = data.chargeTemplateId
      ? data.chargeTemplateId
      : null;
    const feeScheduleId = data.feeScheduleId ? data.feeScheduleId : null;
    createDraftEstimate({
      variables: {
        patientId: patient.id,
        chargeTemplateId,
        feeScheduleId,
        existingEstimateId: existingEstimate?.id,
        draftEstimateProviderId: providerId,
        draftEstimateDateOfService: date,
        draftEstimateName: estimateName,
        policyIds: allPolicyIds.map((id) => ({
          insurancePolicyId: id,
          cashPay: !id,
          active: selectedPolicyIds.includes(id),
        })),
        inputs: data.insuranceServiceLines.flatMap((policy) => {
          return policy.serviceLines.map((line) => {
            const primaryLine = primaryServiceLines.find(
              (psl) => psl.chargemaster.id === line.chargemaster.id
            );
            return {
              insurancePolicyId: policy.policyId,
              chargemasterId: line.chargemaster.id,
              // Use the primary's units if the policy is not the primary
              units: parseInt(primaryLine?.units ?? line.units!),
              // Use the primary's allowed amount if the policy is not the primary
              allowedAmount: mapNullable((val: string) =>
                toCents(parseFloat(val))
              )(primaryLine?.allowedAmount ?? line.allowedAmount)!,
              copay: mapNullable((val: string) => toCents(parseFloat(val)))(
                line.copay
              ),
              coinsurance: mapNullable((val: string) =>
                percentageToDecimal(val)
              )(line.coinsurance),
              remainingDeductible: mapNullable((val: string) =>
                toCents(parseFloat(val))
              )(line.remainingDeductible),
              deductibleApplies: isDefined(line.deductibleApplies)
                ? line.deductibleApplies === "true"
                : null,
              remainingOutOfPocket: mapNullable((val: string) =>
                toCents(parseFloat(val))
              )(line.remainingOutOfPocket),
              coverageBenefitId: line.coverageBenefitId
                ? line.coverageBenefitId
                : null,
              // Use the primary's scheduled fee if the policy is not the primary
              scheduledServiceFeeId:
                primaryLine?.scheduledServiceFeeId ??
                line.scheduledServiceFeeId ??
                null,
            };
          });
        }),
      },
      onCompleted: (data) => {
        if (existingEstimate) {
          toast.success("Estimate updated");
        } else {
          toast.success("Estimate created");
        }

        // Track the successful creation or update of a draft estimate
        const chargeTemplateName = chargeTemplateId
          ? chargeTemplates.find((ct) => ct.id === chargeTemplateId)?.name ||
            null
          : null;

        analytics?.track("Draft Estimate Created", {
          patientId: patient.id,
          estimateId: data.createDraftEstimate.estimate.id,
          isUpdate: !!existingEstimate,
          chargeTemplateName,
          providerId,
          feeScheduleId,
          totalPatientResponsibility: estimate.totalPatientResponsibility,
          totalInsuranceResponsibility:
            estimate.insuranceResponsibilities.reduce(
              (sum, resp) => sum + resp.insurance,
              0
            ),
        });

        onComplete?.();
      },
      onError: () => {
        toast.error("Failed to create estimate");
      },
    });
  };

  return (
    <div className="text-left relative">
      {/* Background color split screen for large screens */}
      <div
        className="absolute left-0 top-0 hidden h-full w-2/3 bg-white lg:block -z-10"
        aria-hidden="true"
      />
      <div
        className="absolute right-0 top-0 hidden h-full w-1/3 bg-gray-50 lg:block -z-10"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid grid-cols-1 py-0 lg:gap-x-16 lg:grid-cols-3 px-8">
        <section
          aria-labelledby="estimate-summary"
          className="bg-gray-50 px-4 py-4 sm:px-6 order-2 lg:col-start-3 lg:row-start-1 lg:bg-transparent lg:px-0 lg:pb-16"
        >
          <div className="mx-auto max-w-lg lg:max-w-none">
            <h2
              id="estimate-summary"
              className="text-lg font-medium text-gray-900 flex gap-2 items-center"
            >
              Estimate Summary
              {isCalculating && (
                <OvalSpinner className="h-4 w-4 text-gray-700" />
              )}
            </h2>

            <dl className="space-y-6 border-b border-gray-200 pt-4 text-sm font-medium text-gray-900 lg:block">
              <div className="flex items-center justify-between pb-4">
                <dt className="text-gray-600 text-base">
                  Total Allowed Amount
                </dt>
                <dd>{formatUSD(totalAllowedAmount)}</dd>
              </div>
            </dl>

            <ul
              role="list"
              className="divide-y divide-gray-200 text-sm font-medium text-gray-900"
            >
              {estimate.insuranceResponsibilities
                .filter((resp) => resp.insurancePolicy)
                .map((resp, i) => (
                  <li
                    key={resp.insurancePolicy!.id}
                    className="flex items-start space-x-2 py-2"
                  >
                    <div className="flex-auto text-left space-y-1">
                      <h3>{resp.insurancePolicy!.payer.name}</h3>
                      <div className="flex gap-2">
                        <p className="text-gray-500">
                          {resp.insurancePolicy!.memberId}
                        </p>
                        <p className="text-gray-500">
                          {i === 0 ? "Primary" : "Secondary"}
                        </p>
                      </div>
                    </div>
                    <p className="flex-none text-base font-medium">
                      {formatUSD(-resp.insurance)}
                    </p>
                  </li>
                ))}
            </ul>

            <dl className="space-y-2 text-sm font-medium text-gray-900 lg:block">
              <div className="flex items-center justify-between border-t border-gray-200 py-4">
                <dt className="text-base">Total Insurance Responsibility</dt>
                <dd className="text-base">
                  {formatUSD(-totalInsuranceResponsibility)}
                </dd>
              </div>
            </dl>

            <dl className="space-y-2 border-t border-gray-200 pt-2 text-sm font-medium text-gray-900 lg:block">
              <div className="flex items-center justify-between">
                <dt className="text-gray-600">Copay</dt>
                <dd>{formatUSD(estimate.copayAmount)}</dd>
              </div>

              <div className="flex items-center justify-between">
                <dt className="text-gray-600">Coinsurance</dt>
                <dd>{formatUSD(estimate.coinsuranceAmount)}</dd>
              </div>

              <div className="flex items-center justify-between">
                <dt className="text-gray-600">Towards Deductible</dt>
                <dd>{formatUSD(estimate.deductibleAmount)}</dd>
              </div>

              <div className="flex items-center justify-between">
                <dt className="text-gray-600">
                  Covered by Out of Pocket Limit
                </dt>
                <dd>{formatUSD(estimate.outOfPocketAmount)}</dd>
              </div>

              <div className="flex items-center justify-between">
                <dt className="text-gray-600">Other</dt>
                <dd>{formatUSD(estimate.otherAmount ?? 0)}</dd>
              </div>

              <div className="flex items-center justify-between border-t border-gray-200 pt-6">
                <dt className="text-base">Total Patient Responsibility</dt>
                <dd className="text-base">
                  {formatUSD(estimate.totalPatientResponsibility)}
                </dd>
              </div>
            </dl>

            <div className="py-8">
              <SubmitButton
                type="button"
                disabled={!isValid}
                loading={createDraftEstimateResult.loading}
                onClick={() => {
                  handleConfirm();
                }}
              >
                Save {formatUSD(estimate.totalPatientResponsibility)} Estimate
              </SubmitButton>
            </div>
          </div>
        </section>

        <Form {...form}>
          <form className="space-y-2 col-span-2">
            <section aria-labelledby="charges">
              <div className="flex flex-col gap-2">
                {chargesDifferAlertOpen && (
                  <ChargesDifferAlert
                    setOpen={setChargesDifferAlertOpen}
                    onConfirm={() => {
                      resetForm();
                    }}
                  />
                )}
                {benefitsDifferAlertOpen && (
                  <BenefitsDifferAlert
                    setOpen={setBenefitsDifferAlertOpen}
                    onConfirm={() => {
                      resetForm();
                    }}
                  />
                )}
                {policiesChangedAlertOpen && (
                  <PoliciesChangedAlert
                    setOpen={setPoliciesChangedAlertOpen}
                    onConfirm={() => {
                      resetForm();
                    }}
                  />
                )}
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex justify-between">
                  <div>
                    <div className="mt-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {charges.fields.map((field, idx) => {
                          return (
                            <ChargemasterBadge
                              key={field.id}
                              chargemaster={field.chargemaster}
                              trigger={
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-gray-800 border hover:bg-gray-50">
                                  {chargemasterDisplay(field.chargemaster)}
                                  <button
                                    type="button"
                                    className="group relative ml-1 -mr-1 h-3.5 w-3.5 rounded-sm hover:bg-gray-500/20"
                                    onClick={() =>
                                      removeCharge(field.chargemaster.id)
                                    }
                                  >
                                    <span className="sr-only">Remove</span>
                                    <svg
                                      viewBox="0 0 14 14"
                                      className="h-3.5 w-3.5 stroke-gray-600/50 group-hover:stroke-gray-600/75"
                                    >
                                      <path d="M4 4l6 6m0-6l-6 6" />
                                    </svg>
                                    <span className="absolute -inset-1" />
                                  </button>
                                </span>
                              }
                            />
                          );
                        })}
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="text-xs px-2 py-1 h-6"
                          onClick={() => {
                            setCodeSearchOpen(true);
                          }}
                        >
                          Add Charge +
                        </Button>
                      </div>
                    </div>
                  </div>
                  {!chargesDifferAlertOpen &&
                    !benefitsDifferAlertOpen &&
                    !policiesChangedAlertOpen && (
                      <ResetActions
                        onReset={() => resetForm()}
                        onClear={() => {
                          form.reset({
                            charges: [],
                            chargeTemplateId: "",
                            feeScheduleId: "",
                            insuranceServiceLines:
                              data.insuranceServiceLines.map((policy) => ({
                                ...policy,
                                serviceLines: [],
                              })),
                          });
                        }}
                        defaultChargeTemplateId={defaultChargeTemplate?.id}
                        defaultFeeScheduleId={defaultFeeSchedule?.id}
                        onChargeTemplateChange={onChargeTemplateChange}
                        onFeeScheduleChange={onFeeScheduleChange}
                        form={form}
                      />
                    )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div>
                  <label
                    htmlFor="charges"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Or select Charge Template
                  </label>
                  <div className="mt-1">
                    <FormField
                      control={form.control}
                      name="chargeTemplateId"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ChargeTemplateCombobox
                              value={field.value}
                              chargeTemplates={chargeTemplates.toSorted(
                                (a, b) => {
                                  // Sort the selected to the top
                                  if (a.id === field.value) {
                                    return -1;
                                  }
                                  if (b.id === field.value) {
                                    return 1;
                                  }
                                  return a.name.localeCompare(b.name);
                                }
                              )}
                              onSelect={(id) => {
                                onChargeTemplateChange(id);
                                // Pass on the change event to the react-hook-form field
                                field.onChange(id);
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="feeScheduleId"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Fee Schedule
                  </label>
                  <div className="mt-1">
                    <FormField
                      control={form.control}
                      name="feeScheduleId"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FeeScheduleCombobox
                              value={field.value}
                              feeSchedules={feeSchedules.toSorted((a, b) => {
                                // Sort the selected to the top
                                if (a.id === field.value) {
                                  return -1;
                                }
                                if (b.id === field.value) {
                                  return 1;
                                }
                                return a.name.localeCompare(b.name);
                              })}
                              onSelect={(id) => {
                                onFeeScheduleChange(id);
                                // Pass on the change event to the react-hook-form field
                                field.onChange(id);
                              }}
                              defaultValue={field.value}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </section>

            <div className="mt-4 flow-root overflow-auto">
              <div className="mx-auto">
                <table className="w-full text-left">
                  <thead className="border-b border-gray-300 text-gray-900">
                    <tr>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900"
                      >
                        Charge
                      </th>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900 w-16"
                      >
                        Units
                      </th>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900 table-cell w-72"
                      >
                        Benefit
                      </th>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900 table-cell w-24"
                      >
                        Copay
                      </th>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900 table-cell"
                      >
                        Coinsurance
                      </th>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900 table-cell w-24"
                      >
                        Deductible
                      </th>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900 table-cell w-20"
                      >
                        Ded Applies
                      </th>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900 table-cell w-24"
                      >
                        OOP
                      </th>
                      <th
                        scope="col"
                        className="py-1 px-2 text-left text-sm font-semibold text-gray-900 w-24 sm:pr-0"
                      >
                        Allowed Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {insurancePolicies.fields.map((field, index) => {
                      const currentPolicy =
                        patient.insurancePolicies.find(
                          (p) => p.id === field.policyId
                        ) ?? null;
                      const priorityOrder =
                        selectedPolicyIds.findIndex(
                          (id) => id === field.policyId
                        ) + 1;

                      return (
                        <DraftPolicyServiceLineFormRows
                          key={field.id}
                          patient={patient}
                          field={field}
                          policy={currentPolicy}
                          policyIdx={index}
                          policiesLength={insurancePolicies.fields.length}
                          providerId={providerId}
                          swapPolicies={insurancePolicies.swap}
                          className={cn(
                            "border-b border-gray-200",
                            priorityOrder > 0 ? "" : "hidden"
                          )}
                          priorityOrder={priorityOrder}
                          allowedAmountsLoading={allowedAmountsLoading}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </form>
        </Form>
      </div>
      {codeSearchOpen && (
        <CodeSearchCommand
          open={codeSearchOpen}
          setOpen={setCodeSearchOpen}
          onSelect={(cm) => {
            if (cm) {
              const chargemaster = {
                ...cm.primaryChargemaster,
                description: cm.primaryChargemaster.description ?? undefined,
                chargemasterGroupId:
                  cm.primaryChargemaster.chargemasterGroupId ?? undefined,
              };
              charges.append({
                chargemaster: cm.primaryChargemaster,
              });
              form.setValue("chargeTemplateId", "");
              form.setValue(
                "insuranceServiceLines",
                data.insuranceServiceLines.map((policy) => {
                  return {
                    ...policy,
                    serviceLines: [
                      ...policy.serviceLines,
                      {
                        existing: false,
                        chargemaster,
                        units: "1",
                        coverageBenefitId: undefined,
                        copay: "",
                        coinsurance: "",
                        remainingDeductible: "",
                        deductibleApplies: "",
                        remainingOutOfPocket: "",
                        allowedAmount: "",
                        scheduledServiceFeeId: undefined,
                      },
                    ],
                  };
                })
              );
              // Lookup allowed amounts of new code
              lookupAllowedAmounts(feeScheduleId ?? null, [cm.id]);
            }
          }}
        />
      )}
    </div>
  );
};

const DraftPolicyServiceLineFormRows: React.FC<{
  patient: IPatient;
  field: FieldArrayWithId<z.infer<typeof FormSchema>, "insuranceServiceLines">;
  policyIdx: number;
  policiesLength: number;
  className?: string;
  policy: InsurancePolicy | null;
  providerId: string | null;
  priorityOrder: number;
  swapPolicies: (fromIdx: number, toIdx: number) => void;
  allowedAmountsLoading: boolean;
}> = ({
  patient,
  field,
  policyIdx,
  policiesLength,
  className,
  policy,
  providerId,
  priorityOrder,
  swapPolicies,
  allowedAmountsLoading,
}) => {
  const form = useFormContext<z.infer<typeof FormSchema>>();

  const serviceLines = useFieldArray({
    control: form.control,
    name: `insuranceServiceLines.${policyIdx}.serviceLines`,
  });
  const mostRecentEligibility = policy?.mostRecentEligibilityRequest;

  const visitChargemasterGroupIds = serviceLines.fields
    .map((field) => field.chargemaster.chargemasterGroupId)
    .filter(isDefined)
    .sort();

  return (
    <Fragment>
      <tr className="border-b border-gray-200">
        <td colSpan={9}>
          <div className="flex gap-2 items-center py-2 w-full">
            <FormField
              control={form.control}
              name={`insuranceServiceLines.${policyIdx}.enabled`}
              render={({ field }) => (
                <FormItem>
                  <FormControl className="flex items-center">
                    <Checkbox
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      {...field}
                      defaultChecked={field.value === "true"}
                      onCheckedChange={(checked) => {
                        field.onChange(checked.toString());
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`insuranceServiceLines.${policyIdx}.policyId`}
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input placeholder="Enter Policy Id" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            {policy ? (
              <>
                {priorityOrder > 0 && (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border bg-gray-100 border-gray-50 text-[0.625rem] font-medium text-gray-400">
                    {priorityOrder}
                  </span>
                )}

                <div>{policy?.payer?.name}</div>

                <div className="text-gray-500 italic">{policy?.memberId}</div>

                {policy.planType && <PlanTypeSelect insurancePolicy={policy} />}

                {mostRecentEligibility ? (
                  <EligibilityStatusBadge
                    eligibilityRequest={mostRecentEligibility}
                    patientId={patient.id}
                    insurancePolicyId={policy.id}
                  />
                ) : (
                  <Badge text="Unverified" variant="info" clickable={false} />
                )}
                {policy && (
                  <NetworkBadge
                    orgPayerNetworkStatus={policy.appointmentInNetwork}
                  />
                )}

                <div className="grow flex gap-2 justify-end">
                  {policyIdx > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        swapPolicies(policyIdx, policyIdx - 1);
                      }}
                    >
                      <ArrowUpIcon className="h-4 w-4 text-gray-500 hover:text-gray-900" />
                    </button>
                  )}
                  {policyIdx < policiesLength - 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        swapPolicies(policyIdx, policyIdx + 1);
                      }}
                    >
                      <ArrowDownIcon className="h-4 w-4 text-gray-500 hover:text-gray-900" />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-gray-500 italic">Cash Pay</div>
            )}
          </div>
        </td>
      </tr>
      {serviceLines.fields.map((field, index) => {
        return (
          <LineItemFormRow
            key={field.id}
            field={field}
            index={index}
            policyIdx={policyIdx}
            priorityOrder={priorityOrder}
            patient={patient}
            providerId={providerId}
            policy={policy}
            className={className}
            allowedAmountsLoading={allowedAmountsLoading}
            visitChargemasterGroupIds={visitChargemasterGroupIds}
            draft={true}
          />
        );
      })}
    </Fragment>
  );
};

const DraftEstimatesDialogButton: React.FC<
  React.PropsWithChildren<{ patient: IPatient }>
> = ({ patient }) => {
  const apollo = useApolloClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [wizardDialogOpen, setWizardDialogOpen] = useState(false);
  const [dialogState, setDialogState] = useState<{
    view: "create" | "wizard";
    formData?: {
      provider: string | null;
      date: Date | undefined;
      templateId: string | null;
      estimateName: string | null;
      recommendedFeeScheduleId: string | null;
    };
  }>({ view: "create" });

  // Check for openDialog parameter in URL when component mounts
  // This is used when navigating from the Universal Command Bar's "Create Draft Estimate" action
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openDialogParam = queryParams.get("openDialog");

    if (openDialogParam === "create") {
      console.info("Opening create dialog from URL parameter");
      // Open the create dialog automatically
      setCreateDialogOpen(true);
      setDialogState({ view: "create" });

      // Remove the parameter from URL to prevent reopening on refresh
      const newParams = new URLSearchParams(location.search);
      newParams.delete("openDialog");

      // Use navigate to update URL without full page reload
      window.history.replaceState(
        null,
        "",
        `${location.pathname}?${newParams.toString()}`
      );
    }
  }, [location.search]);

  return (
    <>
      <Button
        type="button"
        className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-9 px-3 py-2"
        onClick={() => setCreateDialogOpen(true)}
      >
        Create Estimate
      </Button>

      <CreateEstimateDialog
        patient={patient}
        open={createDialogOpen}
        setOpen={setCreateDialogOpen}
        onComplete={(formData) => {
          setCreateDialogOpen(false); // Close the create dialog
          setDialogState({
            view: "wizard",
            formData,
          });
          setWizardDialogOpen(true); // Open the wizard dialog
        }}
      />
      {dialogState.view === "wizard" && dialogState.formData && (
        <DraftEstimateWizardDialog
          patient={patient}
          selectedProvider={dialogState.formData.provider}
          selectedDate={dialogState.formData.date ?? null}
          selectedTemplate={dialogState.formData.templateId}
          selectedEstimateName={dialogState.formData.estimateName}
          selectedRecommendedFeeScheduleId={
            dialogState.formData.recommendedFeeScheduleId
          }
          setOpen={(open) => {
            // Only allow closing via X button by ignoring false values from clicking outside
            if (open) setWizardDialogOpen(open);
          }}
          onComplete={() => {
            setDialogState({ view: "create" });
            setWizardDialogOpen(false); // Close the wizard dialog
            apollo.refetchQueries({
              include: [RETRIEVE_DRAFT_ESTIMATES],
            });
          }}
        />
      )}
    </>
  );
};

interface DraftEstimateTableToolbarProps<TData extends { id: string }> {
  table: ReactTable<TData>;
  patient: IPatient;
}

const DraftEstimateTableToolbar = ({
  table,
  patient,
}: DraftEstimateTableToolbarProps<DraftEstimateRow>) => {
  const isFiltered = table.getState().columnFilters.length > 0;

  const rows = table.getFilteredRowModel().rows;
  const providerOptions = Array.from(
    new Set(rows.map((row) => row.original.providerName).filter(isDefined))
  ).map((provider) => ({
    value: provider,
    label: provider,
  }));
  const chargeTemplateOptions = Array.from(
    new Set(rows.map((row) => row.original.chargeTemplate).filter(isDefined))
  ).map((chargeTemplate) => ({
    value: chargeTemplate,
    label: chargeTemplate,
  }));
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-1 items-center flex-wrap gap-2">
        {table.getColumn("providerName") && (
          <DataTableFacetedFilter
            column={table.getColumn("providerName")}
            title="Provider"
            options={providerOptions}
          />
        )}
        {table.getColumn("chargeTemplate") && (
          <DataTableFacetedFilter
            column={table.getColumn("chargeTemplate")}
            title="Charge Template"
            options={chargeTemplateOptions}
          />
        )}
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
      <DraftEstimatesDialogButton patient={patient} />
    </div>
  );
};

interface DataTableProps<TValue> {
  columns: ColumnDef<DraftEstimateRow, TValue>[];
  defaultColumnFilters?: ColumnFiltersState;
  data: DraftEstimateRow[];
}

export type DraftEstimateRow = {
  id: string;
  createdAt: string;
  chargeTemplate: string | null;
  providerName: string | null;
  estimateName: string | null;
  dateOfService: string | null;
  patientResponsibility: number;
};

export const DraftEstimateDataTable = <TValue,>({
  columns,
  data,
  defaultColumnFilters,
  children,
  loading,
  loadingMore,
  patient,
}: DataTableProps<TValue> & {
  children?: (table: ReactTable<DraftEstimateRow>) => React.ReactElement;
  loading?: boolean;
  loadingMore?: boolean;
  patient: IPatient;
}) => {
  const [selectedRow, setSelectedRow] =
    React.useState<DraftEstimateRow | null>();
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    defaultColumnFilters ?? []
  );
  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
      globalFilter,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    autoResetPageIndex: false,
  });

  return (
    <>
      {children && children(table)}
      <div className="space-y-4">
        <div className="w-full">
          <DraftEstimateTableToolbar table={table} patient={patient} />
        </div>
        <div className="border bg-white max-h-[80vh] overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="sticky top-0 z-10 p-2"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => {
                      setSelectedRow(row.original);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className="whitespace-nowrap p-2"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center p-2"
                  >
                    <div className="w-full flex justify-center">
                      <OvalSpinner />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center p-2"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="w-full">
          <DataTablePagination table={table} loading={loading || loadingMore} />
        </div>
      </div>
    </>
  );
};

export const DraftEstimateInsurancePolicyDisclosureRow: React.FC<{
  insurancePolicy: InsurancePolicy;
  patientId: string;
  providerServiceConfigurations: ProviderServiceConfiguration[];
  selectedProviderId: string | null;
}> = ({
  insurancePolicy,
  patientId,
  providerServiceConfigurations,
  selectedProviderId,
}) => {
  const user = useUser();
  const [isPending, setIsPending] = React.useState(false);
  const apollo = useApolloClient();
  const coverageBenefits = insurancePolicy.inNetwork
    ? insurancePolicy.inNetworkBenefits
    : insurancePolicy.outOfNetworkBenefits;
  const providerId =
    // If the insurance policy has a default eligibility provider, use that
    insurancePolicy.payer.defaultEligibilityProvider?.id ??
    // If the user's location has a default eligibility provider, use that
    user?.activeLocation.defaultEligibilityProvider?.id ??
    // Otherwise, use the selected provider
    selectedProviderId;
  return (
    <Disclosure as="div">
      <Disclosure.Button className="w-full flex justify-between pt-1 gap-2">
        {({ open }) => (
          <>
            <div className="grow">
              <InsurancePolicyRow
                insurancePolicy={insurancePolicy}
                priority={insurancePolicy.priority}
                patientId={patientId}
                onEligibilityRequestCompleted={(data) => {
                  if (
                    data.eligibilityRequest?.status &&
                    data.eligibilityRequest.status !==
                      EligibilityRequestStatus.InProgress
                  ) {
                    setIsPending(false);
                    toast.info("Eligibility request completed");
                    apollo.refetchQueries({
                      include: [GET_PATIENT],
                    });
                  }
                }}
              />
            </div>
            <ChevronUpIcon
              className={cn(
                "h-6 w-6 text-gray-500 transition ease-in-out duration-200 group-hover:text-gray-400 cursor-pointer",
                open && "rotate-180"
              )}
            />
          </>
        )}
      </Disclosure.Button>

      <Disclosure.Panel className="py-2">
        <div className="text-sm font-medium text-gray-900">Benefits</div>
        <div className="flex flex-wrap gap-1 pt-1">
          {[...providerServiceConfigurations]
            .sort(
              (a, b) =>
                (a.priorityOrder ?? Infinity) - (b.priorityOrder ?? Infinity)
            )
            .map((config) => {
              const cb = coverageBenefits.find(
                (b) => b.providerServiceConfiguration.id === config.id
              );
              if (!cb) {
                return (
                  <ServiceBenefitHoverCard
                    key={config.id}
                    trigger={
                      <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-600/10">
                        {config.shortName ?? config.name}
                      </span>
                    }
                    coverageBenefit={null}
                    patientId={patientId}
                    providerServiceConfiguration={config}
                    insurancePolicy={insurancePolicy}
                    inNetwork={insurancePolicy.inNetwork}
                  />
                );
              }
              if (cb.empty) {
                return (
                  <ServiceBenefitHoverCard
                    key={cb.id}
                    trigger={
                      <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-500 ring-1 ring-inset ring-gray-600/10">
                        {cb.providerServiceConfiguration.shortName ??
                          cb.providerServiceConfiguration.name}
                      </span>
                    }
                    coverageBenefit={cb}
                    patientId={patientId}
                    providerServiceConfiguration={
                      cb.providerServiceConfiguration
                    }
                    insurancePolicy={insurancePolicy}
                    inNetwork={cb.networkStatus}
                  />
                );
              }
              if (cb.nonCovered) {
                return (
                  <ServiceBenefitHoverCard
                    key={cb.id}
                    trigger={
                      <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                        {cb.providerServiceConfiguration.shortName ??
                          cb.providerServiceConfiguration.name}
                      </span>
                    }
                    coverageBenefit={cb}
                    patientId={patientId}
                    providerServiceConfiguration={
                      cb.providerServiceConfiguration
                    }
                    insurancePolicy={insurancePolicy}
                    inNetwork={cb.networkStatus}
                  />
                );
              }

              return (
                <ServiceBenefitHoverCard
                  key={cb.id}
                  trigger={
                    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                      {cb.providerServiceConfiguration.shortName ??
                        cb.providerServiceConfiguration.name}
                    </span>
                  }
                  coverageBenefit={cb}
                  patientId={patientId}
                  providerServiceConfiguration={cb.providerServiceConfiguration}
                  insurancePolicy={insurancePolicy}
                  inNetwork={cb.networkStatus}
                />
              );
            })}

          <div className="w-full flex justify-end">
            <VerifyButton
              insurancePolicy={insurancePolicy}
              providerId={providerId}
              isPending={isPending}
              setIsPending={setIsPending}
              patientId={patientId}
            />
          </div>
        </div>
      </Disclosure.Panel>
    </Disclosure>
  );
};

export const VerifyButton: React.FC<{
  insurancePolicy: InsurancePolicy;
  patientId: string;
  providerId: string | null;
  isPending: boolean;
  setIsPending: (isPending: boolean) => void;
}> = ({ insurancePolicy, providerId, isPending, setIsPending, patientId }) => {
  const [requestEligibility, requestEligibilityResult] = useMutation<
    RequestEligibility,
    RequestEligibilityVariables
  >(REQUEST_ELIGIBILITY, {
    onCompleted: () => {
      toast.info("Eligibility request sent");
    },
  });

  return (
    <Button
      onClick={() => {
        if (providerId) {
          requestEligibility({
            variables: {
              insurancePolicyId: insurancePolicy.id,
              providerId,
            },
          });
          setIsPending(true);
        }
      }}
      disabled={requestEligibilityResult.loading || isPending}
    >
      {providerId ? (
        requestEligibilityResult.loading || isPending ? (
          <>
            <Spinner className="mr-2 h-4 w-4 animate-spin" />
            Verifying...
          </>
        ) : (
          "Verify Benefits"
        )
      ) : (
        <Link
          to={`/patients/${patientId}/eligibilities/new`}
          state={{ policy: insurancePolicy.id }}
        >
          Open new eligibility form
        </Link>
      )}
    </Button>
  );
};
